import type { Schema } from '@dealdesk/api-schema';
import { z } from 'zod';

import { zMoney, zRequiredString } from '@/business';

type Rule = Schema<'StaffCommissionRule'>;
const ANY = 'any';
const PERCENT = /^\d+(\.\d{1,4})?$/;

/** A tier value is a percent for percentage rules and an AED amount for fixed rules; checked in superRefine. */
const tier = z.object({ fromVolume: zMoney(), value: z.string() });

export const ruleSchema = z
  .object({
    name: zRequiredString().pipe(z.string().min(3, 'form.required')),
    kind: z.enum(['per_deal', 'target_bonus']),
    creditRole: z.enum(['sales_officer', 'team_leader', 'sales_support']),
    staffType: z.enum([ANY, 'employee', 'outsourced']),
    bankId: z.string(),
    product: z.string(),
    schemeId: z.string(),
    calc: z.enum(['percentage', 'fixed']),
    basis: z.enum(['disbursed', 'booked', 'collected_bank_commission']),
    clawbackPolicy: z.enum(['proportional', 'full', 'none']),
    rate: z.string(),
    amount: z.string(),
    tiers: z.array(tier),
    validFrom: z.string({ error: 'form.required' }).regex(/^\d{4}-\d{2}-\d{2}$/, 'form.required'),
    validTo: z.string().nullable(),
    priority: z.number({ error: 'form.required' }).int().min(0, 'staffCommission:rules.form.priorityInvalid').max(99, 'staffCommission:rules.form.priorityInvalid'),
  })
  .superRefine((v, ctx) => {
    const isPct = v.kind === 'per_deal' && v.calc === 'percentage';
    if (!v.tiers.length) {
      if (isPct && !PERCENT.test(v.rate)) ctx.addIssue({ code: 'custom', path: ['rate'], message: 'staffCommission:rules.form.rateInvalid' });
      if (!isPct && !zMoney().safeParse(v.amount).success) ctx.addIssue({ code: 'custom', path: ['amount'], message: 'form.invalidAmount' });
    }
    v.tiers.forEach((tr, i) => {
      const ok = isPct ? PERCENT.test(tr.value) : zMoney().safeParse(tr.value).success;
      if (!ok) ctx.addIssue({ code: 'custom', path: ['tiers', i, 'value'], message: isPct ? 'staffCommission:rules.form.rateInvalid' : 'form.invalidAmount' });
    });
    if (new Set(v.tiers.map((x) => x.fromVolume)).size !== v.tiers.length) ctx.addIssue({ code: 'custom', path: ['tiers'], message: 'staffCommission:rules.form.tiersDuplicate' });
    if (v.validTo && v.validTo < v.validFrom) ctx.addIssue({ code: 'custom', path: ['validTo'], message: 'staffCommission:rules.form.dateOrder' });
    if (v.basis === 'collected_bank_commission' && v.staffType !== 'outsourced') ctx.addIssue({ code: 'custom', path: ['basis'], message: 'staffCommission:rules.form.collectedOutsourcedOnly' });
    if (v.kind === 'target_bonus' && v.creditRole !== 'sales_officer') ctx.addIssue({ code: 'custom', path: ['creditRole'], message: 'staffCommission:rules.form.targetCredit' });
  });
export type RuleValues = z.infer<typeof ruleSchema>;

export function toRuleFormValues(r?: Rule): RuleValues {
  return {
    name: r?.name ?? '',
    kind: r?.kind ?? 'per_deal',
    creditRole: r?.creditRole ?? 'sales_officer',
    staffType: r?.staffType ?? ANY,
    bankId: r?.bankId ?? ANY,
    product: r?.product ?? ANY,
    schemeId: r?.schemeId ?? ANY,
    calc: r?.calc ?? 'percentage',
    basis: r?.basis ?? 'disbursed',
    clawbackPolicy: r?.clawbackPolicy ?? 'proportional',
    rate: r?.rate ?? '',
    amount: r?.amount ?? '',
    tiers: r?.tiers.map((x) => ({ ...x })) ?? [],
    validFrom: r?.validFrom ?? '',
    validTo: r?.validTo ?? null,
    priority: r?.priority ?? 0,
  };
}

const orNull = (v: string) => (v === ANY || v === '' ? null : v);

export function toRuleRequest(v: RuleValues): Schema<'StaffCommissionRuleInput'> {
  const calc = v.kind === 'target_bonus' ? 'fixed' : v.calc;
  return {
    name: v.name.trim(),
    kind: v.kind,
    creditRole: v.kind === 'target_bonus' ? 'sales_officer' : v.creditRole,
    staffType: v.staffType === ANY ? null : v.staffType,
    bankId: v.kind === 'target_bonus' ? null : orNull(v.bankId),
    product: v.kind === 'target_bonus' ? null : (orNull(v.product) as Schema<'ProductCode'> | null),
    schemeId: v.kind === 'target_bonus' ? null : orNull(v.schemeId),
    calc,
    basis: v.basis,
    clawbackPolicy: v.clawbackPolicy,
    rate: calc === 'percentage' && !v.tiers.length ? v.rate : null,
    amount: calc === 'fixed' && !v.tiers.length ? v.amount : null,
    tiers: v.kind === 'target_bonus' ? [] : v.tiers,
    validFrom: v.validFrom,
    validTo: v.validTo || null,
    priority: v.priority,
  };
}

export const ANY_SCOPE = ANY;
